import React from "react";
import styled from "styled-components";
import { Mountain, ArrowUpRight, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";

const HomeContainer = styled.div`
  min-height: 100vh;
  width: 100vw;
  display: flex;
  background: #fff;
  font-family: "Inter", sans-serif;

  @media (max-width: 1200px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1800px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex: 1;

  @media (max-width: 1200px) {
    flex-direction: column;
    align-items: center;
  }
`;

const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 120px;
  padding-top: 70px;
  max-width: 50%;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    padding: 20px 40px;
    max-width: 100%;
    width: 100%;
    text-align: center;
    align-items: center;
  }
`;

const CenteredContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (max-width: 1200px) {
    align-items: center;
  }
`;

const RightPanel = styled.div`
  flex: 1;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 50px;

  @media (max-width: 1200px) {
    min-height: auto;
    padding: 40px 40px 40px 80px;
  }
  @media (max-width: 800px) {
    padding: 40px 20px 40px 20px;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const MountainBackground = styled.img`
  width: 90%;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  object-fit: contain;
  margin-top: -100px;
  @media (max-width: 1200px) {
    margin-top: 0px;
  }
`;

const StatsOverlay = styled.div`
  position: absolute;
  top: -50px;
  left: -10px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 14px;
  padding: 22px;
  padding-bottom: 5px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(10px);
  min-width: 252px;

  @media (max-width: 800px) {
    position: static;
    top: auto;
    left: auto;
    margin-top: -80px;
    min-width: 252px;
  }
`;

const StatsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ResortName = styled.h3`
  margin: 0 0 4px 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #000;
`;

const ResortLocation = styled.p`
  margin: 0 0 10px 0;
  font-size: 0.9rem;
  color: #666;
`;

const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.span`
  font-size: 1.2rem;
  font-weight: 700;
  color: #000;
  margin-bottom: 2px;
`;

const StatLabel = styled.span`
  font-size: 0.8rem;
  color: #666;
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  svg {
    width: 16px;
    height: 16px;
    color: #666;
  }
`;

const Logo = styled.div`
  font-size: 2rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 40px;
  color: #000;
  align-self: flex-start;
  cursor: default;
`;

const Headline = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin: 0 0 20px 0;
  color: #000;
  line-height: 1.1;
  @media (max-width: 800px) {
    margin: 0px;
    padding: 40px 0px 20px 0px;
    font-size: 2.5rem;
  }
`;

const Description = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin: 0 0 40px 0;
  line-height: 1.6;
  max-width: 500px;
`;

const LoginButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: #000;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  padding: 14px 28px;
  cursor: pointer;
  transition: all 0.2s;
  width: fit-content;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);

  &:hover {
    transform: translateY(1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`;

export default function LoggedOutHome() {
  const navigate = useNavigate();

  return (
    <HomeContainer>
      <ContentWrapper>
        <LeftPanel>
          <Logo>
            <Mountain size={32} style={{ marginRight: -7, marginBottom: 5 }} />
            LastRun
          </Logo>

          <CenteredContent>
            <Headline>Map the Stoke</Headline>

            <Description>
              Upload your GPX data to get a 3D view of everywhere you’ve skied,
              plus deep stats: most-skied runs, resort completion rates, and a
              checklist of untouched lines.
            </Description>

            <LoginButton onClick={() => navigate("/login")}>
              Try it now
            </LoginButton>
          </CenteredContent>
        </LeftPanel>

        <RightPanel>
          <ImageContainer>
            <MountainBackground src="/map.png" alt="Mammoth Mountain Map" />
            <StatsOverlay>
              <ResortName>Mammoth</ResortName>
              <ResortLocation>California</ResortLocation>

              <StatItem>
                <StatContent>
                  <StatValue>217</StatValue>
                  <StatLabel>runs</StatLabel>
                </StatContent>
                <StatIcon>
                  <ArrowUpRight />
                </StatIcon>
              </StatItem>

              <StatItem>
                <StatContent>
                  <StatValue>599</StatValue>
                  <StatLabel>km</StatLabel>
                </StatContent>
                <StatIcon>
                  <ArrowUpRight />
                </StatIcon>
              </StatItem>

              <StatItem>
                <StatContent>
                  <StatValue>65.4k</StatValue>
                  <StatLabel>vertical meter</StatLabel>
                </StatContent>
                <StatIcon>
                  <ArrowUpRight />
                </StatIcon>
              </StatItem>

              <StatItem>
                <StatContent>
                  <StatValue>3.9k</StatValue>
                  <StatLabel>longest run (m)</StatLabel>
                </StatContent>
                <StatIcon>
                  <Info />
                </StatIcon>
              </StatItem>

              <StatItem>
                <StatContent>
                  <StatValue>24</StatValue>
                  <StatLabel>steepest slope (°)</StatLabel>
                </StatContent>
                <StatIcon>
                  <Info />
                </StatIcon>
              </StatItem>

              <StatItem>
                <StatContent>
                  <StatValue>71%</StatValue>
                  <StatLabel>runs completed</StatLabel>
                </StatContent>
                <StatIcon>
                  <ArrowUpRight />
                </StatIcon>
              </StatItem>
            </StatsOverlay>
          </ImageContainer>
        </RightPanel>
      </ContentWrapper>
    </HomeContainer>
  );
}
